import styled from "@emotion/styled";
import { forwardRef } from "react";
import { alpha, useTheme } from "@kaltura/mediaspace-shared-styled";
import { formatEntryThumbnailUrl, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";

export interface DraggableResultsImageProps {
    imageUrl: string;
    itemsCount?: number;
}

const Container = styled("div")({
    position: "fixed",
    top: "-1000px",
    left: "-1000px",
});

const StyledImage = styled("div")(({ theme }) => ({
    width: theme.typography.pxToRem(150),
    height: theme.typography.pxToRem(85),
    borderRadius: theme.kaltura.shape.roundness2,
    boxShadow: "0px 0px 0px 0.8px rgba(0, 0, 0, 0.20), 0px 3px 24px -6px rgba(0, 0, 0, 0.20)",
    position: "relative",
}));

const StyledBackImage = styled(StyledImage)(({ theme }) => ({
    position: "absolute",
    left: theme.typography.pxToRem(12),
    opacity: 0.7,
    transform: "rotate(8deg)",
}));

const CountBadge = styled("div")(({ theme }) => ({
    position: "absolute",
    bottom: theme.spacing(1),
    left: theme.spacing(1),
    backgroundColor: alpha(theme.palette.common.black, 0.6),
    color: theme.palette.common.white,
    padding: theme.spacing(0.75),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.kaltura.typography.fontWeightBold,
    borderRadius: theme.kaltura.shape.roundness1,
}));

const baseClassName = "kms-ds-my-media-draggable-search-result";

/**
 * image to render while dragging search results
 * relevant when areSearchResultsDraggable prop is true in SearchResults
 * see example use-case in my-media folders feature
 */
export const DraggableResultsImage = forwardRef<HTMLDivElement, DraggableResultsImageProps>(
    ({ imageUrl, itemsCount = 1 }, ref) => {
        // align with MediaCard's thumbnail dimensions - reuse cached images
        const theme = useTheme();
        const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
        const width = isSmallScreen ? 158 : 186;
        const height = isSmallScreen ? 90 : 106;

        const backgroundImageStyle = {
            background: `url(${formatEntryThumbnailUrl(imageUrl, width, height)}) lightgray center / cover no-repeat`,
        };

        return (
            <Container ref={ref} className={`${baseClassName}-container`} aria-hidden={true}>
                {itemsCount > 1 && (
                    <StyledBackImage className={`${baseClassName}-back-image`} style={backgroundImageStyle} />
                )}
                <StyledImage className={`${baseClassName}-image`} style={backgroundImageStyle}>
                    {itemsCount > 1 && (
                        <CountBadge className={`${baseClassName}-count-badge`}>
                            {translate("%1 selected", [itemsCount])}
                        </CountBadge>
                    )}
                </StyledImage>
            </Container>
        );
    }
);

export default DraggableResultsImage;
